export type AccountEntryState = Readonly<{
    phase: 'entry' | 'proof' | 'pending' | 'created' | 'signed-in';
    busy: boolean;
    code?: string;
    challengeId?: string;
}>;
type Begin = {
    idempotencyKey: string;
    expectedVersion: null;
    input: {
        email: string;
        locale: string;
        legalCountry: string;
        nonce: string;
    };
};
type Finish = {
    idempotencyKey: string;
    expectedVersion: 1;
    input: {
        challengeId: string;
        nonce: string;
        proof: string;
        password: string;
    };
};
const random = () => Array.from(crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join('');
const plain = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
/** Account credentials/proof are memory-only. No URL/localStorage/log/replay
 * store. Explicit check/resume is the ONLY retry path; never automatically
 * repeat delivery/finish/sign-in. Disposal aborts and invalidates callbacks. */
export function createAccountEntryClient(onAuthenticated: (token: string) => void, fetcher: typeof fetch = fetch) {
    let state: AccountEntryState = { phase: 'entry', busy: false }, beginCommand: Begin | undefined, finishCommand: Finish | undefined, challenge: Readonly<{
        id: string;
        expiresAt: string;
    }> | undefined, dead = false, epoch = 0;
    let controller: AbortController | undefined;
    const snapshot = () => Object.freeze({ ...state });
    async function post(path: string, body: unknown) { const r = await fetcher('/api/thistleglass/account/' + path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), cache: 'no-store', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer', signal: controller?.signal }); let value: unknown; try {
        value = await r.json();
    }
    catch {
        return { status: r.status, value: undefined };
    } return { status: r.status, value }; }
    async function work(effect: (generation: number) => Promise<void>) { if (dead || state.busy)
        throw Error('ACCOUNT_REQUEST_IN_PROGRESS'); const generation = epoch; controller = new AbortController(); state = { ...state, busy: true, code: undefined }; try {
        await effect(generation);
    }
    catch {
        if (!dead && generation === epoch)
            state = { ...state, phase: 'pending', code: 'ACCOUNT_STATUS_UNKNOWN' };
    }
    finally {
        if (!dead && generation === epoch) {
            state = { ...state, busy: false };
            controller = undefined;
        }
    } return snapshot(); }
    function current(generation: number) { return !dead && generation === epoch; }
    function observeError(status: number, value: unknown) { if (plain(value) && typeof value.code === 'string' && /^[A-Z_]{1,80}$/.test(value.code) && [400, 403, 409, 429, 503].includes(status)) {
        state = { ...state, code: value.code };
        return value.code;
    } state = { ...state, code: 'ACCOUNT_STATUS_UNKNOWN' }; return 'ACCOUNT_STATUS_UNKNOWN'; }
    async function sendBegin(generation: number) {
        if (!beginCommand)
            throw Error('NO_ACCOUNT_REQUEST');
        const r = await post('signup/begin', beginCommand);
        if (!current(generation))
            return;
        if (r.status === 202 && plain(r.value) && Object.keys(r.value).sort().join(',') === 'challengeId,expiresAt,state,version' && typeof r.value.challengeId === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(r.value.challengeId) && r.value.version === 1 && typeof r.value.expiresAt === 'string' && Number.isFinite(Date.parse(r.value.expiresAt)) && ['proof-required', 'unknown'].includes(String(r.value.state))) {
            challenge = { id: r.value.challengeId, expiresAt: r.value.expiresAt };
            state = { phase: 'proof', busy: true, challengeId: challenge.id, ...(r.value.state === 'unknown' ? { code: 'ACCOUNT_DELIVERY_UNKNOWN' } : {}) };
            return;
        }
        const code = observeError(r.status, r.value);
        const notAdmitted = (r.status === 400 && code === 'INVALID_INPUT') || (r.status === 403 && code === 'ORIGIN_DENIED') || (r.status === 429 && code === 'ACCOUNT_RATE_LIMITED') || (r.status === 503 && code === 'ACCOUNT_DELIVERY_UNCONFIGURED');
        if (notAdmitted)
            beginCommand = undefined;
        state = { ...state, phase: notAdmitted ? 'entry' : 'pending' };
    }
    async function sendFinish(generation: number) {
        if (!finishCommand)
            throw Error('NO_ACCOUNT_REQUEST');
        const r = await post('signup/finish', finishCommand);
        if (!current(generation))
            return;
        if (r.status === 200 && plain(r.value) && Object.keys(r.value).join(',') === 'code' && r.value.code === 'ACCOUNT_REQUEST_FINISHED') {
            finishCommand = undefined;
            beginCommand = undefined;
            challenge = undefined;
            state = { phase: 'created', busy: true };
            return;
        }
        const code = observeError(r.status, r.value);
        if (r.status === 400 || r.status === 403) {
            finishCommand = undefined;
            state = { ...state, phase: 'proof', code };
        }
        else
            state = { ...state, phase: 'pending', code };
    }
    return Object.freeze({ snapshot,
        begin(email: string, legalCountry: string, locale: string) { return work(async (generation) => { if (beginCommand || finishCommand || challenge)
            throw Error('EXISTING_ACCOUNT_REQUEST'); beginCommand = { idempotencyKey: random(), expectedVersion: null, input: { email, legalCountry, locale, nonce: random() } }; await sendBegin(generation); }); },
        finish(proof: string, password: string) { return work(async (generation) => { if (!challenge || !beginCommand || finishCommand)
            throw Error('NO_ACCOUNT_REQUEST'); finishCommand = { idempotencyKey: random(), expectedVersion: 1, input: { challengeId: challenge.id, nonce: beginCommand.input.nonce, proof, password } }; await sendFinish(generation); }); },
        check() { return work(async (generation) => { if (!challenge) {
            await sendBegin(generation);
            return;
        } if (!beginCommand)
            throw Error('NO_ACCOUNT_REQUEST'); const r = await post('signup/status', { challengeId: challenge.id, nonce: beginCommand.input.nonce }); if (!current(generation))
            return; if (r.status === 200 && plain(r.value) && r.value.state === 'completed' && r.value.code === 'ACCOUNT_REQUEST_FINISHED' && Object.keys(r.value).sort().join(',') === 'code,state') {
            finishCommand = undefined;
            beginCommand = undefined;
            challenge = undefined;
            state = { phase: 'created', busy: true };
            return;
        } if (r.status === 200 && plain(r.value) && r.value.state === 'pending' && r.value.retryable === false && Object.keys(r.value).sort().join(',') === 'retryable,state') {
            state = { ...state, phase: finishCommand ? 'pending' : 'proof' };
            return;
        } observeError(r.status, r.value); state = { ...state, phase: 'pending' }; }); },
        resumeSameRequest() { return work(async (generation) => { if (finishCommand)
            await sendFinish(generation);
        else
            await sendBegin(generation); }); },
        signIn(email: string, password: string) { return work(async (generation) => { if (finishCommand || challenge)
            throw Error('EXISTING_ACCOUNT_REQUEST'); const r = await post('signin', { email, password }); if (!current(generation))
            return; if (r.status === 200 && plain(r.value) && Object.keys(r.value).sort().join(',') === 'expiresIn,token' && r.value.expiresIn === 3600 && typeof r.value.token === 'string' && /^[a-f0-9]{64}$/.test(r.value.token)) {
            onAuthenticated(r.value.token);
            state = { phase: 'signed-in', busy: true };
            return;
        } observeError(r.status, r.value); }); },
        dispose() { dead = true; epoch++; controller?.abort(); controller = undefined; beginCommand = undefined; finishCommand = undefined; challenge = undefined; state = { phase: 'entry', busy: false }; },
    });
}
